<script setup lang="ts">
import {
	computed,
	inject,
	nextTick,
	onMounted,
	onUnmounted,
	Ref,
	ref,
	toRefs,
	useTemplateRef,
	watch,
} from "vue";
import {
	defaultRangeExtractor,
	useVirtualizer,
	type Range as VirtualRange,
} from "@tanstack/vue-virtual";
import {
	FlexRender,
	getCoreRowModel,
	getSortedRowModel,
	Header,
	type Row,
	SortingState,
	useVueTable,
} from "@tanstack/vue-table";
import {
	extractDimensionValuesFromRow,
	generateColumns,
	hierarchyRootIsTheTotal,
	mapDimensionNameToIndex,
	transformDataForPivot,
} from "@/modules/explorer/components/Metrics/MetricHelper";
import SingleMetricValueContextMenu from "@/modules/explorer/components/Metrics/SingleMetricValueContextMenu.vue";
import { Cell } from "@tanstack/table-core";
import {
	documentKey,
	derivedDocumentKey,
} from "@/modules/explorer/ExplorerUtils";
import { TableContext } from "@/modules/explorer/components/Metrics/Table/context";
import { useDashboardStore } from "@/modules/dashboards/store";
import axisConfigurationDialog from "@/modules/explorer/components/AxisConfiguration/Dialog.vue";
import DataTableDO = App.Data.Explorer.DataTableDO;
import DocumentVerticalDTO = App.Data.DocumentVerticalDTO;
import DataTableMetricValueType = App.Enums.DataTableMetricValueType;
import SingleMetricPickDTO = App.Data.Explorer.SingleMetric.SingleMetricPickDTO;
import TableAlertPropertiesDTO = App.Data.Alerts.TableAlertPropertiesDTO;
import SingleMetricType = App.Data.Explorer.SingleMetric.SingleMetricType;
import DeleteColumnPayload = App.Data.Explorer.SingleMetric.DeleteColumnPayload;
import { useExplorerStore } from "@/modules/explorer/store";

const props = withDefaults(
	defineProps<{
		data: Record<string, string>[];
		dataTable: DataTableDO;
		metrics: DocumentVerticalDTO[];
		isDashboard: boolean;
		maxHeight: number | null;
		mainHierarchyLabel: string;
		periodColors?: Record<string, string>;
		printMode?: boolean;
		isReorderingDataTable?: boolean;
		columnsIncludedAfterCustomSql?: Array<string>;
		customSqlRowClasses: Array<string>;
	}>(),
	{}
);

const emit = defineEmits<{
	(e: "setColumnAlert", type: TableAlertPropertiesDTO): void;
	(e: "update", type: DataTableDO, reorderingDataTable?: boolean): void;
}>();

const sorting = ref<SortingState>([]);

/**
 * Single source of truth for the row height: the virtualizer estimate, the
 * print rows and the cell styling (via the --table-row-h custom property) must
 * all agree, or virtualized rows drift out of position while scrolling.
 */
const ROW_HEIGHT = 28;

const isPrinting = useDashboardStore().dashboardState.isPrinting;

const {
	data,
	dataTable,
	metrics,
	isDashboard,
	maxHeight,
	mainHierarchyLabel,
	periodColors,
	printMode,
	isReorderingDataTable,
	columnsIncludedAfterCustomSql,
	customSqlRowClasses,
} = toRefs(props);

const document = inject(documentKey);
const derivedDocument = inject(derivedDocumentKey);

const setSorting = (sortingUpdater: (prev: SortingState) => SortingState) => {
	sorting.value = sortingUpdater(sorting.value);
};

const getSortingHandler = (e: Event, fn: any) => {
	return fn(e);
};

const comparisonPeriods = computed(() => {
	return [
		document.value.date_comparison.comparisonLabel || "Y",
		derivedDocument.value?.date_comparison.isBudgetComparisonActive
			? "Bud"
			: null,
		...document.value.date_comparison.periods.map((period) => `Y${period}`),
		...document.value.date_comparison.customPeriods.map(
			(period) => period.name
		),
	]
		.filter(Boolean)
		.sort((a, b) => {
			if (a === "Bud") return 1;
			if (b === "Bud") return -1;
			return a.localeCompare(b, undefined, { numeric: true });
		});
});

const pivotResult = computed(() => {
	if (!dataTable.value.pivot_dimension) {
		return { pivotedData: data.value, uniquePivotValues: [] };
	}
	return transformDataForPivot(data.value, dataTable.value);
});

const tableData = computed(() => pivotResult.value.pivotedData);

const columns = computed(() => {
	return generateColumns(
		dataTable.value,
		metrics.value,
		comparisonPeriods.value,
		document.value.date_comparison.isBudgetComparisonActive,
		true,
		false,
		pivotResult.value.uniquePivotValues,
		columnsIncludedAfterCustomSql.value
	);
});

watch(sorting, (newVal) => {
	dataTable.value.sort_by = newVal[0]?.id ?? null;
	dataTable.value.sort_direction = newVal[0]?.desc ? "desc" : "asc";
	emit("update", dataTable.value, true);
});

const table = useVueTable({
	data: tableData,
	get columns() {
		return columns.value;
	},
	state: {
		get sorting() {
			return sorting.value;
		},
	},
	onSortingChange: setSorting,
	getCoreRowModel: getCoreRowModel(),
	getSortedRowModel: getSortedRowModel(),
});

const isSummaryRow = (row: Row<Record<string, string>>): boolean =>
	Boolean(row.original.isTotalRow) ||
	(hierarchyRootIsTheTotal(dataTable.value) &&
		mainHierarchyLabel.value !== "" &&
		row.original.horizontal === mainHierarchyLabel.value);

// table.getRowModel() is not reactive. We add a check around data.value to make sure the computed updates.
// table.getRowModel() is not reactive. We add a check around tableData.value to make sure the computed updates.
const rows = computed(() => {
	if (tableData.value.length === 0) return [];

	const allRows = table.getRowModel().rows;

	return [
		...allRows.filter(isSummaryRow),
		...allRows.filter((row) => !isSummaryRow(row)),
	];
});

/** `rows` puts a summary row first, so pinning it means keeping index 0. */
const pinsSummaryRow = computed(() => {
	const first = rows.value[0];

	return first !== undefined && isSummaryRow(first);
});

const parentRef = ref<HTMLElement | null>(null);
const tableRef = ref<HTMLElement | null>(null);
const virtualizerOptions = computed(() => ({
	count: rows.value.length,
	getScrollElement: () => parentRef.value,
	estimateSize: () => ROW_HEIGHT,
	overscan: 20,
	rangeExtractor: (range: VirtualRange) => {
		const indexes = defaultRangeExtractor(range);

		return pinsSummaryRow.value && indexes[0] !== 0 ? [0, ...indexes] : indexes;
	},
}));

const tableContext = defineModel<TableContext>();

const virtualizer = useVirtualizer(virtualizerOptions);
const virtualRows = computed(() => virtualizer.value.getVirtualItems());
const totalSize = computed(() => Math.ceil(virtualizer.value.getTotalSize()));

/**
 * The virtualized rows are kept in place by two spacer rows instead of a
 * transform, so the table's own height matches the full scroll range. A sticky
 * thead can only stay put inside its table box, and giving the table an
 * explicit height would stretch the few rendered rows to fill it.
 */
const paddingTop = computed(() => {
	const first = virtualRows.value[0];

	if (!first) {
		return 0;
	}

	// A pinned total row still occupies its own box at the top of the body, so
	// the spacer only has to cover the gap between it and the first row of the
	// scrolled window.
	const isPinned = pinsSummaryRow.value && first.index === 0;
	const firstScrolled = isPinned ? virtualRows.value[1] : first;

	if (!firstScrolled) {
		return 0;
	}

	return firstScrolled.start - (isPinned ? first.size : 0);
});
const paddingBottom = computed(() => {
	const lastRow = virtualRows.value.at(-1);

	return lastRow ? totalSize.value - lastRow.end : 0;
});
const spacerColSpan = computed(
	() =>
		table.getVisibleLeafColumns().length +
		(dataTable.value.show_row_numbers ? 1 : 0)
);

const headerRef = useTemplateRef("headerRef");
/** Rounded up, for the container height and the height emitted to a card. */
const headerHeight = ref(0);
/** Unrounded, for the total row's sticky offset. */
const headerHeightExact = ref(0);

const columnWidths = ref<number[]>([]);
const isMeasuringWidths = ref(false);

/** How many of a column's longest values are offered to the measuring pass. */
const RULER_CANDIDATES_PER_COLUMN = 3;

/**
 * The rows holding the longest values of each column.
 *
 * Measuring only the rendered window sizes a column to the rows that happen to
 * be on screen, so a longer value further down the response had nowhere to go.
 * Putting these rows in the DOM for the measuring frame lets the browser size
 * every column against the whole response instead.
 *
 * Length is a proxy for width, exact for the numeric columns because the table
 * renders tabular figures, and approximate for text — hence more than one
 * candidate per column. The winners overlap heavily, so the deduplicated set is
 * normally a handful of rows.
 */
const rulerRowIndexes = computed(() => {
	void columns.value;

	const indexes = new Set<number>();

	for (const column of table.getVisibleLeafColumns()) {
		// Kept as a running shortlist, shortest first, rather than sorting every
		// row of the response once per column: this runs over every refresh.
		const longest: { index: number; length: number }[] = [];

		rows.value.forEach((row, index) => {
			const length = String(row.original[column.id] ?? "").length;

			if (
				longest.length === RULER_CANDIDATES_PER_COLUMN &&
				length <= longest[0].length
			) {
				return;
			}

			if (longest.length === RULER_CANDIDATES_PER_COLUMN) {
				longest[0] = { index, length };
			} else {
				longest.push({ index, length });
			}

			longest.sort((a, b) => a.length - b.length);
		});

		longest.forEach((candidate) => indexes.add(candidate.index));
	}

	return [...indexes];
});
const hasFrozenColumns = computed(
	() => !isPrinting && columnWidths.value.length > 0
);
const frozenTableWidth = computed(() =>
	columnWidths.value.reduce((total, width) => total + width, 0)
);

/**
 * Identifies the pass in flight. Two overlapping passes would otherwise clear
 * each other's ruler rows, and the survivor would measure without them.
 */
let measurementToken = 0;

const freezeColumnWidths = async () => {
	if (isPrinting) {
		return;
	}

	const token = ++measurementToken;

	// Back to auto for one frame, with the widest rows of the response in the
	// DOM, so the browser measures against the content rather than the widths it
	// was last given.
	columnWidths.value = [];
	isMeasuringWidths.value = true;
	await nextTick();

	// Measuring before the webfont has landed gives fallback-font widths, which
	// are then too narrow for the text that finally renders. `document` is the
	// injected DocumentDO here, hence window.
	await window.document.fonts?.ready;

	// A newer pass started while the fonts were loading; it owns the ruler rows
	// and the widths from here on.
	if (token !== measurementToken) {
		return;
	}

	// The lowest header row holds one cell per visible leaf column.
	const leafRow = headerRef.value?.querySelector("tr:last-child");

	if (!leafRow) {
		isMeasuringWidths.value = false;

		return;
	}

	// getBoundingClientRect is precise to a fraction of a pixel but carries any
	// surrounding scale; offsetWidth is scale-free but whole pixels. Dividing one
	// by the other undoes the scale, and rounding up is what keeps a column from
	// ending up a fraction short of the text it has to hold — which is a wrapped
	// header, and a row taller than the virtualizer believes.
	const scale =
		(leafRow as HTMLElement).getBoundingClientRect().width /
			(leafRow as HTMLElement).offsetWidth || 1;

	columnWidths.value = [...leafRow.children].map((cell) =>
		Math.ceil((cell as HTMLElement).getBoundingClientRect().width / scale)
	);

	isMeasuringWidths.value = false;
};

// A new response or a different column set is the moment to re-measure; that
// is a layout change the user made, unlike scrolling.
watch([data, columns], () => void freezeColumnWidths(), { flush: "post" });

// A horizontal scrollbar is drawn inside the container's content box, so the
// rows it covers would push a vertical scrollbar in unless its height is added
// back. It has to be measured rather than computed: scrollWidth and clientWidth
// are plain DOM reads that Vue cannot track, and the height differs per
// platform and with `scrollbar-width` anyway.
const horizontalScrollbarHeight = ref(0);

const measureHorizontalScrollbar = () => {
	const element = parentRef.value;

	if (!element) {
		return;
	}

	// For a box that scrolls horizontally this difference is exactly the
	// scrollbar's height — 0 where the platform draws it as an overlay.
	horizontalScrollbarHeight.value =
		element.scrollWidth > element.clientWidth
			? element.offsetHeight - element.clientHeight
			: 0;
};

onMounted(() => {
	void freezeColumnWidths();

	const observer = new ResizeObserver(() => {
		measureHorizontalScrollbar();

		if (headerRef.value) {
			const rect = headerRef.value.getBoundingClientRect();

			// offsetHeight is already rounded. This can lead to a very small scrollbar being shown, if it's rounded down.
			// We can use getBoundingClientRect() to get the fractional height, but this takes scaling into account.
			// We don't want that, as we emit non-scaled numbers.
			// As a hack we check if getBoundingClientRect().height is fractional, and in that case add 1 extra pixel.
			headerHeight.value = headerRef.value.offsetHeight;
			if (rect.height - Math.floor(rect.height) > 0.001) {
				headerHeight.value += 1;
			}

			// The sticky offset of the total row is applied inside the same scaled
			// context as the header, so it wants the true fractional height rather
			// than the rounded one — rounding up parks the row a fraction below the
			// header and lets scrolled rows show through the seam. The rect carries
			// any surrounding scale, which the width ratio undoes (as in
			// freezeColumnWidths).
			const scale = rect.width / headerRef.value.offsetWidth || 1;
			headerHeightExact.value = rect.height / scale;
		}
	});
	observer.observe(headerRef.value as HTMLElement, {});

	// The container catches the wrapper resizing, the table catches its own
	// width changing when columns are added, hidden or re-measured — either can
	// bring the horizontal scrollbar in or out.
	if (parentRef.value) {
		observer.observe(parentRef.value);
	}
	if (tableRef.value) {
		observer.observe(tableRef.value);
	}

	onUnmounted(() => observer.disconnect());
});

const computedHeight = computed(() => {
	// The scrollbar gutter is inside the clamp: maxHeight is the wrapper's own
	// inner height, so the container must not grow past it. Below that, asking
	// for the gutter is what stops the scrollbar squeezing the rows and pulling
	// a second, vertical scrollbar in.
	const sizeWithHeader =
		totalSize.value + headerHeight.value + horizontalScrollbarHeight.value;

	if (maxHeight.value === null) {
		return sizeWithHeader;
	}

	return Math.min(sizeWithHeader, maxHeight.value);
});

const metricSubcolumnCount = computed(
	() =>
		table.getHeaderGroups().at(-1).headers.length -
		dataTable.value.dimensions.length
);

watch(
	[totalSize, headerHeight],
	() => {
		tableContext.value = {
			height: totalSize.value + headerHeight.value,
			headerHeight: headerHeight.value,
		};
	},
	{
		immediate: true,
	}
);

const isContextMenuVisible = ref(false);
const isColumnSelect = ref(false);
const contextMenuClickedEvent = ref<MouseEvent | null>(null);
const tmpSingleMetricConfiguration: Ref<SingleMetricPickDTO> = ref();
const clickedColumn = ref(null);
const openContextMenu = (
	event: MouseEvent,
	metricName: string,
	comparisonValue: string,
	dimensionValues: Record<string, any>,
	valueColumn: DataTableMetricValueType,
	forColumn: boolean = false,
	header?: Header<string, string>
) => {
	tmpSingleMetricConfiguration.value = {
		type: "pick",
		table_index: 0,
		metric_name: metricName,
		comparison_value: comparisonValue,
		value_column: valueColumn,
		dimension_values: dimensionValues,
		header: header,
	};

	contextMenuClickedEvent.value = event;
	isContextMenuVisible.value = true;
	isColumnSelect.value = forColumn;
};

const useSingleMetric = () => {
	document.value.single_metric = tmpSingleMetricConfiguration.value;
	tmpSingleMetricConfiguration.value = undefined;

	useExplorerStore().explorerState.activeDataView = "single-metric";
};

const getTableAlertPropertiesDTO = (): TableAlertPropertiesDTO => {
	return {
		table_index: tmpSingleMetricConfiguration.value.table_index,
		metric_name: tmpSingleMetricConfiguration.value.metric_name,
		comparison_value: tmpSingleMetricConfiguration.value.comparison_value,
		value_column: tmpSingleMetricConfiguration.value.value_column,
	};
};

const useColumnMetric = (type: SingleMetricType) => {
	document.value.single_metric = {
		...{
			type,
		},
		...getTableAlertPropertiesDTO(),
	};
	useExplorerStore().explorerState.activeDataView = "single-metric";
};

const setColumnAlert = () => {
	emit("setColumnAlert", getTableAlertPropertiesDTO());
};

const hideColumn = (columnKey: string) => {
	const dt = structuredClone(props.dataTable);
	dt.hidden_columns.push(columnKey);
	emit("update", dt);
};

const pivotColumn = (dimensionKey: string) => {
	const dt = structuredClone(props.dataTable);
	const dimensionIndex = mapDimensionNameToIndex(dimensionKey);
	dt.pivot_dimension = dt.dimensions[dimensionIndex]?.name_clean ?? null;
	emit("update", dt);
};

const unpivotColumn = () => {
	const dt = structuredClone(props.dataTable);
	dt.pivot_dimension = null;
	emit("update", dt);
};

const deleteColumn = (payload: DeleteColumnPayload) => {
	const dt = structuredClone(props.dataTable);
	if (payload.type === "metric") {
		dt.metrics = dt.metrics.filter(
			(metric) => metric.name_clean !== payload.nameClean
		);
	} else if (payload.type === "dimension") {
		dt.dimensions = dt.dimensions.filter(
			(dimension) => dimension.name_clean !== payload.nameClean
		);
	}
	if (
		payload.type === "dimension" &&
		payload.nameClean === dt.pivot_dimension
	) {
		dt.pivot_dimension = null;
	}

	dt.hidden_columns = dt.hidden_columns.filter(
		(c) => !c.startsWith(payload.nameClean + "_")
	);

	emit("update", dt);
};

const handleRowContextMenu = (e, cell: Cell<any, any>) => {
	if (isSummaryRow(cell.row)) {
		return;
	}

	if (isDashboard.value || !cell.column.columnDef.meta?.metricName) {
		return;
	}
	e.preventDefault();

	openContextMenu(
		e,
		cell.column.columnDef.meta?.metricName,
		cell.column.columnDef.meta?.comparison,
		extractDimensionValuesFromRow(dataTable.value, cell.row),
		cell.column.columnDef.meta?.valueType,
		false
	);
};

const handleColumnContextMenu = (e, header: Header<any, any>) => {
	clickedColumn.value = header;
	if (dataTable.value.custom_sql) return;
	if (header.column.depth === 0 && !isDashboard.value) {
		// Top level headers, only delete column button will be visible in the context menu
	} else {
		if (
			isDashboard.value ||
			!header.column.getCanSort() ||
			!header.column.columnDef.meta?.metricName
		) {
			return;
		}
	}

	e.preventDefault();

	openContextMenu(
		e,
		header.column.columnDef.meta?.metricName,
		header.column.columnDef.meta?.comparison,
		{},
		header.column.columnDef.meta?.valueType,
		true,
		header
	);
};

const shouldIndentCell = (cell: Cell<any, any>) => {
	if (table.getState().sorting.length > 0) {
		return false;
	}

	if (!cell.row || !cell.row.original) {
		return false;
	}

	const cellValue = cell.getValue();

	if (
		dataTable.value.custom_sql &&
		typeof cellValue === "string" &&
		cellValue.startsWith("   ")
	) {
		return true;
	}

	if (cell.column.id !== "horizontal") {
		return false;
	}

	if (dataTable.value.dimensions[0].type !== "hierarchy") {
		return false;
	}

	if (cellValue === "Total") return false;
	return cellValue !== mainHierarchyLabel.value;
};

const isDragging = ref(false);
const hoveredColumn = ref(null);
const draggingIndex = ref(null);
const targetIndex = ref(null);
const showColumnsConfigurationDialog = ref(false);
const indexToInsert = ref<number | null>(null);
const dataTableToEdit = ref<App.Data.Explorer.DataTableDO | null>(null);
const insertType = ref<"metrics" | "dimensions">("dimensions");
const draggedHeader = ref(null);

const onDragStart = (header, index: number) => {
	isDragging.value = true;
	draggingIndex.value = index;
	draggedHeader.value = header;
};

const onDragOver = (targetHeader, index: number) => {
	const targetIsDimension = isDimension(targetHeader);
	const draggedIsDimension = isDimension(draggedHeader.value);
	if (draggedIsDimension !== targetIsDimension) {
		return;
	}

	if (!isDraggable(targetHeader, isReorderingDataTable.value)) {
		return;
	}

	targetIndex.value = index;
};

function getLeafColumns(header) {
	if (!header.subHeaders || header.subHeaders.length === 0) {
		return header.isPlaceholder ? [] : [header.column.id];
	}
	return header.subHeaders.flatMap(getLeafColumns);
}

function headerTreeHasMeta(header) {
	if (header.column.columnDef.meta?.metricName) return true;

	if (header.subHeaders?.length) {
		return header.subHeaders.some((child) => headerTreeHasMeta(child));
	}

	return false;
}
function isDimension(header) {
	return header.column.depth === 0 && !headerTreeHasMeta(header);
}

type TableColumn = ReturnType<typeof table.getVisibleLeafColumns>[number];

const getTopLevelColumn = (column: TableColumn): TableColumn => {
	let current = column;

	while (current.parent) {
		current = current.parent;
	}

	return current;
};

/**
 * Ids of the leaf columns that open a metric block, used to draw the vertical
 * rule that separates one metric from the next (and the dimensions from the
 * first metric). Dimension columns carry no metricName, so they never open one.
 */
const groupStartColumnIds = computed(() => {
	// Referenced so the set is rebuilt whenever the column definitions change.
	void columns.value;

	const ids = new Set<string>();
	let previousGroupId: string | null = null;

	table.getVisibleLeafColumns().forEach((column) => {
		const groupId = getTopLevelColumn(column).id;

		if (column.columnDef.meta?.metricName && groupId !== previousGroupId) {
			ids.add(column.id);
		}

		previousGroupId = groupId;
	});

	return ids;
});

const getPeriodColor = (column: TableColumn): string | undefined => {
	// With a single period the colour is the same everywhere and tells the reader
	// nothing, so the bars only appear once there are periods to tell apart.
	if (comparisonPeriods.value.length < 2) {
		return undefined;
	}

	return periodColors.value?.[column.columnDef.meta?.comparison ?? ""];
};

/**
 * Dimension columns are tinted to set them apart from the metric block. Custom
 * SQL tables have no dimensions, so nothing is tinted there.
 */
const dimensionColumnIds = computed(() => {
	void columns.value;

	const ids = new Set<string>();

	if (dataTable.value.custom_sql) {
		return ids;
	}

	table.getVisibleLeafColumns().forEach((column) => {
		if (!column.columnDef.meta?.metricName) {
			ids.add(column.id);
		}
	});

	return ids;
});

const isDimensionColumn = (column: TableColumn) => {
	if (column.columns.length === 0) {
		return dimensionColumnIds.value.has(column.id);
	}

	return dimensionColumnIds.value.has(column.getLeafColumns()[0]?.id);
};

const startsGroup = (column: TableColumn) => {
	// Called for every rendered cell, so the leaf lookup is skipped where the
	// column already is the leaf.
	if (column.columns.length === 0) {
		return groupStartColumnIds.value.has(column.id);
	}

	return groupStartColumnIds.value.has(column.getLeafColumns()[0]?.id);
};
function isDraggable(header: any, isReorderingDataTable: boolean) {
	return (
		!dataTable.value.custom_sql &&
		!isDashboard.value &&
		!header.column.parent &&
		!isReorderingDataTable
	);
}

function reOrderDataTable(draggingIndex: number, targetIndex: number) {
	if (draggingIndex === targetIndex) return;
	const dt = structuredClone(props.dataTable);

	const forMetrics = draggingIndex + 1 > dataTable.value.dimensions.length;
	const newDraggingIndex = forMetrics
		? draggingIndex - dt.dimensions.length
		: draggingIndex;
	const newTargetIndex = forMetrics
		? targetIndex - dt.dimensions.length
		: targetIndex;

	const objectToExtractFrom = forMetrics ? dt.metrics : dt.dimensions;
	const [extractedElement] = objectToExtractFrom.splice(newDraggingIndex, 1);
	objectToExtractFrom.splice(newTargetIndex, 0, extractedElement);

	//Loading the data in the background for the dimensions breaks the formatting on switch at the moment.
	emit("update", dt, forMetrics);
}

const onDrop = () => {
	if (draggingIndex.value === null || targetIndex.value === null) return;

	const topLevelHeaders = [...table.getHeaderGroups()[0].headers];

	const [movedGroup] = topLevelHeaders.splice(draggingIndex.value, 1);
	topLevelHeaders.splice(targetIndex.value, 0, movedGroup);

	const newLeafOrder = topLevelHeaders.flatMap(getLeafColumns);

	table.setColumnOrder(newLeafOrder);

	reOrderDataTable(draggingIndex.value, targetIndex.value);

	draggingIndex.value = null;
	targetIndex.value = null;
	draggedHeader.value = null;
};
const onDragEnd = () => {
	draggingIndex.value = null;
	targetIndex.value = null;
	isDragging.value = false;
};

function canSeeAddButton(hoveredColumn, header, headerGroup, isDragging) {
	return (
		!isDashboard.value &&
		hoveredColumn === header.id &&
		!isDragging &&
		(headerGroup.depth === 0 || !header.column.columnDef.meta?.metricName)
	);
}

const configureColumnDialog = (
	index: number | null = null,
	header: Header<any, any>
) => {
	dataTableToEdit.value = props.dataTable;
	indexToInsert.value = index;

	insertType.value = isDimension(header) ? "dimensions" : "metrics";

	showColumnsConfigurationDialog.value = true;
};

const visibleColumnCount = computed(() => {
	return (
		table.getHeaderGroups()[0].headers.length -
		dataTable.value.dimensions.length
	);
});

const insertItem = computed(() => ({
	index: indexToInsert.value,
	type: insertType.value,
	data_table_to_edit: dataTableToEdit.value,
	data_table_index: 0,
}));

const getCustomClass = (jsonStr: string) => {
	const customClassesJsonObj = JSON.parse(jsonStr);
	return customClassesJsonObj.class;
};

const getShouldIndent = (jsonStr: string) => {
	const shouldIndentJsonObj = JSON.parse(jsonStr);
	return shouldIndentJsonObj.shouldIndent;
};

/** The (single) summary row sits at index 0, so data rows are numbered after it. */
const rowNumber = (rowIndex: number): number | string => {
	if (!pinsSummaryRow.value) {
		return rowIndex + 1;
	}

	return rowIndex === 0 ? "-" : rowIndex;
};
</script>

<template>
	<axis-configuration-dialog
		v-if="showColumnsConfigurationDialog"
		:is-visible="showColumnsConfigurationDialog"
		:insert-item="insertItem"
		@close-dialog="showColumnsConfigurationDialog = false"
	/>

	<SingleMetricValueContextMenu
		v-if="isContextMenuVisible"
		v-model="isContextMenuVisible"
		:position="
			contextMenuClickedEvent
				? {
						x: contextMenuClickedEvent.clientX,
						y: contextMenuClickedEvent.clientY,
				  }
				: undefined
		"
		:is-column-select="isColumnSelect"
		:single-metric-configuration="tmpSingleMetricConfiguration"
		:clicked-column="clickedColumn"
		:metric-subcolumn-count="metricSubcolumnCount"
		:visible-column-count="visibleColumnCount"
		:has-custom-sql="!!props.dataTable.custom_sql"
		@useSingleMetric="useSingleMetric"
		@useColumnMetric="useColumnMetric"
		@setColumnAlert="setColumnAlert"
		@delete-column="deleteColumn"
		@hide-column="hideColumn"
		@pivot-column="pivotColumn"
		@unpivot-column="unpivotColumn"
	/>

	<div
		ref="parentRef"
		class="container metrics-data-table"
		style="overflow-x: auto"
		:style="{
			'--table-row-h': `${ROW_HEIGHT}px`,
			height: isPrinting ? 'unset' : `${computedHeight}px`,
			'overflow-y': `${maxHeight === null ? 'hidden' : 'auto'}`,
		}"
		v-bind="$attrs"
	>
		<div>
			<table
				ref="tableRef"
				:class="[
					printMode ? 'w-100' : '',
					hasFrozenColumns ? 'columns-frozen' : '',
				]"
				:style="{
					'--table-header-height': `${headerHeightExact}px`,
					width: hasFrozenColumns ? `${frozenTableWidth}px` : undefined,
				}"
			>
				<colgroup v-if="hasFrozenColumns">
					<col
						v-for="(width, columnIndex) in columnWidths"
						:key="columnIndex"
						:style="{ width: `${width}px` }"
					/>
				</colgroup>
				<thead ref="headerRef">
					<tr
						v-for="(headerGroup, groupIndex) in table.getHeaderGroups()"
						:key="headerGroup.id"
					>
						<th
							v-if="dataTable.show_row_numbers"
							:class="{ 'dimension-column': !dataTable.custom_sql }"
						>
							<div class="header-content">
								{{
									groupIndex === table.getHeaderGroups().length - 1 ? "#" : ""
								}}
							</div>
						</th>
						<th
							v-for="(header, headerIndex) in headerGroup.headers"
							:draggable="isDraggable(header, isReorderingDataTable)"
							@dragstart="onDragStart(header, headerIndex)"
							@dragover.prevent="onDragOver(header, headerIndex)"
							@drop="onDrop()"
							@dragend="onDragEnd()"
							:key="header.id"
							:colspan="header.colSpan"
							:style="{
								width: `${header.getSize()}px`,
								'--period-color': getPeriodColor(header.column),
							}"
							:class="[
								header.column.columnDef.meta?.thClasses,
								startsGroup(header.column) ? 'group-start' : '',
								isDimensionColumn(header.column) ? 'dimension-column' : '',
								getPeriodColor(header.column) ? 'period-color' : '',
								draggingIndex === headerIndex && !header.column.parent
									? 'dragging-column'
									: '',
								targetIndex === headerIndex && !header.column.parent
									? 'drag-target'
									: '',
								hoveredColumn === header.id &&
								!header.column.parent &&
								!isDashboard
									? 'header-hovered'
									: '',
							]"
							@contextmenu="(e) => handleColumnContextMenu(e, header)"
						>
							<div
								v-if="!header.isPlaceholder"
								:draggable="isDraggable(header, isReorderingDataTable)"
								class="header-content"
								:class="[
									isReorderingDataTable &&
									!isDimension(header) &&
									header.depth === 1
										? 'reordering-column'
										: header.column.getCanSort()
										? 'cursor-pointer'
										: '',
								]"
								@dragstart="
									groupIndex === 0 && onDragStart(header, headerIndex)
								"
								@dragover.prevent="onDragOver(header, headerIndex)"
								@drop="onDrop()"
								@dragend="onDragEnd()"
								@click="
									getSortingHandler(
										$event,
										header.column.getToggleSortingHandler()
									)
								"
								@mouseenter="hoveredColumn = header.id"
								@mouseleave="hoveredColumn = null"
							>
								<FlexRender
									:render="header.column.columnDef.header"
									:props="header.getContext()"
								/>
								<v-icon
									v-if="header.column.getCanSort()"
									size="14"
									icon="mdi-arrow-up"
									class="sort-arrow"
									:class="{
										'sort-arrow--descending':
											header.column.getIsSorted() === 'desc',
										'sort-arrow--idle': header.column.getIsSorted() === false,
									}"
								/>
								<v-btn
									v-if="
										canSeeAddButton(
											hoveredColumn,
											header,
											headerGroup,
											isDragging
										) && !dataTable.custom_sql
									"
									size="24"
									:class="[
										'add-column-button',
										{
											'add-column-button--last':
												headerIndex === headerGroup.headers.length - 1,
										},
									]"
									@click.stop="configureColumnDialog(header.index, header)"
									>+
								</v-btn>
							</div>
						</th>
					</tr>
				</thead>
				<tbody>
					<template v-if="isPrinting">
						<tr
							v-for="(row, index) in rows"
							:key="index"
							:style="{ height: `${ROW_HEIGHT}px` }"
							:class="{
								'total-row': pinsSummaryRow && index === 0,
							}"
						>
							<td
								v-if="dataTable.show_row_numbers"
								:class="{ 'dimension-column': !dataTable.custom_sql }"
							>
								{{ rowNumber(index) }}
							</td>
							<td
								v-for="cell in row.getVisibleCells()"
								:key="cell.id"
								:class="[
									startsGroup(cell.column) ? 'group-start' : '',
									isDimensionColumn(cell.column) ? 'dimension-column' : '',
									customSqlRowClasses &&
									customSqlRowClasses[index] &&
									sorting.length === 0
										? [
												getCustomClass(customSqlRowClasses[index]),
												getShouldIndent(customSqlRowClasses[index])
													? 'pl-4'
													: '',
										  ]
										: [
												cell.column.columnDef.meta?.tdClasses,
												shouldIndentCell(cell) ? 'pl-4' : '',
										  ],
								]"
								@contextmenu="(e) => handleRowContextMenu(e, cell)"
							>
								<FlexRender
									:render="cell.column.columnDef.cell"
									:props="cell.getContext()"
								/>
							</td>
						</tr>
					</template>
					<template v-else>
						<tr
							v-if="paddingTop > 0"
							class="spacer-row"
							:style="{ height: `${paddingTop}px` }"
						>
							<td :colspan="spacerColSpan" />
						</tr>
						<tr
							v-for="virtualRow in virtualRows"
							:key="virtualRow.index"
							:style="{ height: `${virtualRow.size}px` }"
							:class="{
								'total-row': pinsSummaryRow && virtualRow.index === 0,
							}"
						>
							<td
								v-if="dataTable.show_row_numbers"
								:class="{ 'dimension-column': !dataTable.custom_sql }"
							>
								{{ rowNumber(virtualRow.index) }}
							</td>
							<td
								v-for="cell in rows[virtualRow.index].getVisibleCells()"
								:key="cell.id"
								:class="[
									startsGroup(cell.column) ? 'group-start' : '',
									isDimensionColumn(cell.column) ? 'dimension-column' : '',
									customSqlRowClasses &&
									customSqlRowClasses[virtualRow.index] &&
									sorting.length === 0
										? [
												getCustomClass(customSqlRowClasses[virtualRow.index]),
												getShouldIndent(customSqlRowClasses[virtualRow.index])
													? 'pl-4'
													: '',
										  ]
										: [
												cell.column.columnDef.meta?.tdClasses,
												shouldIndentCell(cell) ? 'pl-4' : '',
										  ],
								]"
								@contextmenu="(e) => handleRowContextMenu(e, cell)"
							>
								<FlexRender
									:render="cell.column.columnDef.cell"
									:props="cell.getContext()"
								/>
							</td>
						</tr>
						<tr
							v-if="paddingBottom > 0"
							class="spacer-row"
							:style="{ height: `${paddingBottom}px` }"
						>
							<td :colspan="spacerColSpan" />
						</tr>
						<!-- Only while the widths are measured: these carry the longest
						value of every column, so auto layout sizes the columns to the
						whole response instead of the rendered window. -->
						<tr
							v-for="rulerIndex in isMeasuringWidths ? rulerRowIndexes : []"
							:key="`ruler-${rulerIndex}`"
							class="ruler-row"
						>
							<td v-if="dataTable.show_row_numbers">
								{{ rows.length }}
							</td>
							<td
								v-for="cell in rows[rulerIndex].getVisibleCells()"
								:key="cell.id"
							>
								<FlexRender
									:render="cell.column.columnDef.cell"
									:props="cell.getContext()"
								/>
							</td>
						</tr>
					</template>
				</tbody>
			</table>
		</div>
	</div>
</template>

<style lang="scss" scoped>
@media print {
	tr {
		break-inside: avoid-page;
		break-after: auto;
	}
}

.metrics-data-table {
	--table-br: rgba(var(--color-on-surface), 0.1);
	--table-br-strong: rgba(var(--color-on-surface), 0.22);
	--table-cell-px: 12px;
	// Smaller than the cell padding: it only keeps text off the table's edge
	--table-edge-px: 8px;
	--table-header-py: 7px;
	--table-hover: rgba(var(--color-on-surface), 0.04);
	--table-accent: rgb(var(--color-blue-accent));
	scrollbar-width: thin;
	scrollbar-color: rgb(var(--color-secondary)) transparent;

	// The sticky header has to stay opaque, so its state tints are pre-mixed
	// with the surface instead of being laid over it with an alpha channel.
	--table-hover-solid: color-mix(
		in srgb,
		rgb(var(--color-on-surface)) 4%,
		rgb(var(--color-surface))
	);
	--table-hover-solid-strong: color-mix(
		in srgb,
		rgb(var(--color-on-surface)) 7%,
		rgb(var(--color-surface))
	);
	--table-dragging-solid: color-mix(
		in srgb,
		rgb(var(--color-blue-accent)) 8%,
		rgb(var(--color-surface))
	);
	--table-drop-target-solid: color-mix(
		in srgb,
		rgb(var(--color-blue-accent)) 4%,
		rgb(var(--color-surface))
	);
	--table-group-br: rgba(var(--color-on-surface), 0.18);

	// Opaque, because the dimension cells sit under the sticky header too
	--table-dimension-bg: color-mix(
		in srgb,
		rgb(var(--color-on-surface)) 3.5%,
		rgb(var(--color-surface))
	);
}

table {
	font-size: 0.6875rem;
	font-weight: 400;
	line-height: 1.4;
	letter-spacing: 0;
	font-family: "Roboto", sans-serif;
	font-variant-numeric: tabular-nums;
	text-transform: none;
	border-collapse: collapse;

	// Only with a colgroup to read from; without one, fixed layout would size
	// every column from the colspan'd group header row.
	&.columns-frozen {
		table-layout: fixed;
	}

	thead {
		position: sticky;
		top: 0;
		z-index: 2;
	}

	// with border-collapse the cell backgrounds paint over the row group's,
	// so the opaque background has to sit on the th itself
	thead th {
		background-color: rgb(var(--color-surface));
	}

	// border-collapse drops real borders on a sticky header, so it is drawn as an inset shadow
	thead tr:last-child th {
		box-shadow: inset 0 -1px 0 var(--table-br-strong);
	}

	// Vertical rule between metric blocks. Inset shadows again: unlike a border
	// they cost no layout width, so the column sizes stay exactly as measured.
	thead th.group-start,
	tbody td.group-start {
		box-shadow: inset 1px 0 0 var(--table-group-br);
	}

	thead tr:last-child th.group-start {
		box-shadow: inset 0 -1px 0 var(--table-br-strong),
			inset 1px 0 0 var(--table-group-br);
	}

	// The comparison period is carried by a colour bar under the lowest header
	// row, in the colour that period has everywhere else in the app.
	thead tr:last-child th.period-color .header-content::after {
		content: "";
		position: absolute;
		left: var(--table-cell-px);
		right: var(--table-cell-px);
		bottom: 2px;
		height: 2px;
		border-radius: 1px;
		background-color: var(--period-color);
	}

	// Colour separation between the dimensions and the metric block
	.dimension-column {
		background-color: var(--table-dimension-bg);
	}

	th {
		font-size: 0.6875rem;
		font-weight: 600;
		letter-spacing: 0.02em;
		vertical-align: middle;
		transition: background-color 0.12s ease-in-out;

		// Metric headers are centred over their group through their thClasses;
		// everything without one is text and lines up with its cells on the left.
		text-align: left;
	}

	.header-content {
		position: relative;
		width: 100%;
		height: 100%;
		padding: var(--table-header-py) var(--table-cell-px);
	}

	// Keeps the first header aligned with the edge padding of its cells
	th:first-child .header-content {
		padding-left: var(--table-edge-px);
	}

	.header-cell {
		position: relative;
	}

	td:not(:first-child) {
		padding: 0 var(--table-cell-px);
	}
	// Fixed layout lets content spill instead of widening the column, and the
	// dimension labels are the long ones, so they are cut off deliberately.
	td:first-child {
		padding-left: var(--table-edge-px);
		padding-right: var(--table-cell-px);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	// A value that wraps makes its row taller than the ROW_HEIGHT the virtualizer
	// positions everything by, so values stay on one line whatever the width.
	td {
		white-space: nowrap;
	}

	// Lends its intrinsic width to the measuring pass without being seen or
	// taking up a row's worth of height while it does.
	.ruler-row {
		visibility: hidden;

		td {
			height: 0;
			line-height: 0;
			padding-top: 0;
			padding-bottom: 0;
		}
	}

	// Same for the metric headers: "Pred" over two lines is the symptom of a
	// column measured a fraction too narrow, and wrapping hides that.
	thead tr:last-child .header-content {
		white-space: nowrap;
	}

	.header-hovered {
		position: relative;
		border-radius: 4px;
		background-color: var(--table-hover-solid);
		cursor: pointer;
	}

	.header-hovered:hover {
		background-color: var(--table-hover-solid-strong);
	}

	.reordering-column {
		cursor: not-allowed;
		z-index: 1;
	}

	.dragging-column {
		border-left: 1px dashed var(--table-accent);
		border-right: 1px dashed var(--table-accent);
		background-color: var(--table-dragging-solid);
	}

	.drag-target {
		border-left: 2px solid var(--table-accent);
		border-right: 2px solid var(--table-accent);
		background-color: var(--table-drop-target-solid);
	}

	// box-shadow instead of a border so rows keep the exact height the virtualizer assumes
	tbody tr:not(:first-child):not(.spacer-row):not(.total-row) {
		box-shadow: 0px -1px var(--table-br);
	}

	tbody tr:not(.spacer-row):hover td {
		background-image: linear-gradient(
			rgba(var(--color-on-surface), 0.07),
			rgba(var(--color-on-surface), 0.07)
		);
	}
}

// Sticks under the header rather than at the top of the scroll box, and has
// to be opaque: the scrolled rows pass underneath it.
.total-row td {
	top: var(--table-header-height, 0px);
	position: sticky;
	z-index: 1;
	background-color: rgb(var(--color-surface));
	font-weight: 500;
}

// The dimension cell clips its overflow for the ellipsis (see td:first-child),
// which also clips the ::before band below — so the seam leaked in this one
// column. An element's own box-shadow escapes its own overflow, so the band is
// drawn that way here instead, with the colour repeated because box-shadow
// cannot inherit it.
.total-row td.dimension-column {
	background-color: var(--table-dimension-bg);
	box-shadow: 0 -4px 0 var(--table-dimension-bg);
}

// The header height is measured rounded up (see the ResizeObserver), so the row
// can land a fraction of a pixel too low and let rows scroll through the seam.
// This band fills it in the cell's own colour, hidden under the opaque header.
// It stays a pseudo-element for the metric cells: `tbody td.group-start` already
// owns their box-shadow for the group separator, and would win on specificity.
.total-row td::before {
	content: "";
	position: absolute;
	left: 0;
	right: 0;
	bottom: 100%;
	height: 4px;
	background-color: inherit;
	pointer-events: none;
}

.total-row td::after {
	content: "";
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	border-bottom: 1px solid var(--table-br-strong);
	pointer-events: none;
}

// DONT DELETE. Custom css component for Revenue rapport.
.hierarchy-parent {
	background-color: rgba(var(--color-on-surface), 0.04);
	text-align: right;
}

.hierarchy-parent:first-child {
	text-align: left;
}

.hierarchy-child {
	text-align: right;
}

.hierarchy-child:first-child {
	text-align: left;
}

.add-column-button {
	border-radius: 50px;
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	right: -12px;
	min-width: 0;
}

.add-column-button--last {
	right: 0;
}

.add-column-button:hover {
	z-index: 10;
}

.sort-arrow {
	display: inline-flex;
	align-items: center;
	justify-content: end;
	position: absolute;
	margin-left: 4px;
	flex: 0 0 auto;
	transition: transform 150ms ease;

	// Every sortable header keeps the slot, sorted or not. Dropping the arrow
	// out of the flow is what makes the label jump the moment you sort.
	&--idle {
		visibility: hidden;
	}

	&--descending {
		transform: rotate(180deg);
	}
}
</style>
